import { isAxiosError } from "axios";
import { extractDomain, isValidDomain } from "@/utils/domains";

export const NOTE_TAKER_TRANSLATION_PREFIX = "settings.noteTaker";

export const MEETING_BOT_STRATEGY_KEY = "meetingbot.strategy";

// A blank display name makes the bot join as "<account name>'s Notetaker".
export const MEETING_BOT_DISPLAY_NAME_KEY = "meetingbot.display_name";

// The meeting-bot provider rejects bot names longer than this.
export const MEETING_BOT_DISPLAY_NAME_MAX_LENGTH = 100;

export enum AutoJoinMode {
  ALL = "always",
  EXTERNAL_HOSTED = "if_not_host",
  NEVER = "never"
}

export const AUTO_JOIN_MODES = [
  AutoJoinMode.ALL,
  AutoJoinMode.EXTERNAL_HOSTED,
  AutoJoinMode.NEVER
];

// The backend reads an empty or unrecognized strategy as "bot disabled", which
// is also the default for accounts that never set it.
export const toAutoJoinMode = (value: unknown): AutoJoinMode => {
  const normalized = typeof value === "string" ? value.toLowerCase() : "";
  if (normalized === AutoJoinMode.ALL) return AutoJoinMode.ALL;
  if (normalized === AutoJoinMode.EXTERNAL_HOSTED) {
    return AutoJoinMode.EXTERNAL_HOSTED;
  }
  return AutoJoinMode.NEVER;
};

export const toStrategyValue = (mode: AutoJoinMode): string =>
  mode === AutoJoinMode.NEVER ? "" : mode;

// A JSON object with snake_case fields; an empty value means no rules.
export const MEETING_BOT_JOIN_RULES_KEY = "meetingbot.join_rules";

// The backend rejects lists longer than these.
export const JOIN_RULES_MAX_DOMAINS = 200;
export const JOIN_RULES_MAX_KEYWORDS = 50;
export const JOIN_RULES_MAX_KEYWORD_LENGTH = 100;

export interface JoinRules {
  alsoInternalDomains: string[];
  ignoredDomains: string[];
  blockedDomains: string[];
  ignorePersonalEmail: boolean;
  skipTitleKeywords: string[];
}

export const EMPTY_JOIN_RULES: JoinRules = {
  alsoInternalDomains: [],
  ignoredDomains: [],
  blockedDomains: [],
  ignorePersonalEmail: false,
  skipTitleKeywords: []
};

const toStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

// A missing, empty or unreadable value shows no rules rather than failing.
export const parseJoinRules = (value: unknown): JoinRules => {
  if (typeof value !== "string" || !value.trim()) return EMPTY_JOIN_RULES;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return EMPTY_JOIN_RULES;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return EMPTY_JOIN_RULES;
  }
  const stored = parsed as Record<string, unknown>;
  return {
    alsoInternalDomains: toStringList(stored.also_internal_domains),
    ignoredDomains: toStringList(stored.ignored_domains),
    blockedDomains: toStringList(stored.blocked_domains),
    ignorePersonalEmail: stored.ignore_personal_email === true,
    skipTitleKeywords: toStringList(stored.skip_title_keywords)
  };
};

const hasNoRules = (rules: JoinRules): boolean =>
  !rules.ignorePersonalEmail &&
  rules.alsoInternalDomains.length === 0 &&
  rules.ignoredDomains.length === 0 &&
  rules.blockedDomains.length === 0 &&
  rules.skipTitleKeywords.length === 0;

// No rules at all is stored as "", which the backend reads as cleared.
// blocked_domains is written only when set: a settingsapi that predates the
// field answers 422, which would fail every join-rules save.
export const serializeJoinRules = (rules: JoinRules): string =>
  hasNoRules(rules)
    ? ""
    : JSON.stringify({
        also_internal_domains: rules.alsoInternalDomains,
        ignored_domains: rules.ignoredDomains,
        ...(rules.blockedDomains.length > 0 && {
          blocked_domains: rules.blockedDomains
        }),
        ignore_personal_email: rules.ignorePersonalEmail,
        skip_title_keywords: rules.skipTitleKeywords
      });

/**
 * The bare domain a user meant, or null when it is not one. Accepts `@acme.com`,
 * a pasted address (`jane@acme.com`) or a URL, and lowercases the result.
 */
export const normalizeDomain = (value: string): string | null => {
  const domain = extractDomain(value.trim().replace(/^@+/, ""));
  return domain && isValidDomain(domain) ? domain : null;
};

const uniqueBy = (values: string[], keyOf: (value: string) => string) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = keyOf(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Invalid entries are dropped; the input rejects them before they get here.
export const normalizeDomains = (values: string[]): string[] =>
  uniqueBy(
    values
      .map(normalizeDomain)
      .filter((domain): domain is string => domain !== null),
    (domain) => domain
  );

// Matching is case-insensitive, so keywords differing only in case are one.
export const normalizeKeywords = (values: string[]): string[] =>
  uniqueBy(
    values.map((keyword) => keyword.trim()).filter(Boolean),
    (keyword) => keyword.toLowerCase()
  );

/** The backend's message for a value it rejected (422), if it sent one. */
export const getRejectedValueMessage = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const detail: unknown = error.response.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  // FastAPI's own request validation answers with a list of errors.
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  return null;
};
